import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  mergeMap,
  withLatestFrom,
  tap,
  map,
} from 'rxjs/operators';
import { StoreService } from '@core/services';
import { ConfiguracionGeneral, Local, LocalForm } from '@core/models';
import { selectBusinessSetting, selectStoreForm, selectStoreId } from '@store/selectors';
import {
  STORE_CREATE,
  STORE_CREATE_COMPLETED,
  STORE_CREATE_VIEW,
  STORE_EDIT_MODE,
  STORE_FIND_ALL,
  STORE_FIND_ALL_SUCCESS,
  STORE_UPDATE_LOADING,
  STORE_FAILURE,
  STORE_UPDATE_VIEW,
  STORE_UPDATE_ID,
  STORE_UPDATE,
  STORE_UPDATE_COMPLETED,
  STORE_REMOVE_COMPLETED,
  STORE_FIND,
  STORE_FIND_SUCCESS,
  NOTIFICATION_SHOW,
  SERVICE_FAILURE,
  STORE_ACTIVATE,
  STORE_ACTIVATE_COMPLETED,
  STORE_DEACTIVATE_COMPLETED,
  STORE_DEACTIVATE,
  STORE_CREATE_DUPLICATE,
  STORE_FIND_ALL_DROPDOWNS,
  STORE_FIND_ALL_DROPDOWNS_SUCCESS
} from '@store/actions';
import { transformStoreForm } from '@core/helpers';

@Injectable()
export class StoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private storeService: StoreService
  ) {}

  createView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_CREATE_VIEW),
      mergeMap(() => [
        STORE_EDIT_MODE({ editMode: true, isEditing: false }),
      ])
    )
  );

  updateView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_UPDATE_VIEW),
      mergeMap(({ storeId }) => [
        STORE_UPDATE_ID({ storeId }),
        STORE_EDIT_MODE({ editMode: true, isEditing: true }),
      ])
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_FIND),
      mergeMap(({ id }) => {
        this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
        return this.storeService.find(id).pipe(
          mergeMap(({ data }) => {
            const [store] = data as Local[];
            return [
              STORE_FIND_SUCCESS({ store }),
              STORE_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              STORE_FAILURE({ error }),
              STORE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_FIND_ALL),
      mergeMap(({ name, resellerId, accountId, stateId, includesInactive }) => {
        this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
        return this.storeService.findAll({ name, resellerId, accountId, stateId, includesInactive }).pipe(
          mergeMap(({ data }) => {
            const storeList = data as Local[];
            return [
              STORE_FIND_ALL_SUCCESS({ storeList }),
              STORE_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              STORE_FAILURE({ error }),
              STORE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_CREATE),
      withLatestFrom(this.store.select(selectStoreForm)),
      filter(([_, storeForm]) => !!storeForm),
      map(([_, storeForm]) => ({ storeForm: storeForm as LocalForm })),
      mergeMap(({ storeForm }) => {
        this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
        return this.storeService.create(storeForm).pipe(
          mergeMap(({data}) => {
            
            const [local] = data;
            
            return [
              NOTIFICATION_SHOW({
                notification: {
                  type: 'success',
                  title: 'Success',
                  detail: 'Store created successfully',
                },
              }),
              STORE_CREATE_DUPLICATE({ targetLocal: storeForm.storeForCopy, originLocal: local.idLocal }),
              STORE_CREATE_COMPLETED(),
              STORE_UPDATE_LOADING({ loading: false }),
              STORE_FIND_ALL({}),
            ]
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              STORE_FAILURE({ error }),
              STORE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  createComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(STORE_CREATE_COMPLETED),
        tap(() => {
          this.storeService.createCompleted();
        })
      ),
    { dispatch: false }
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_UPDATE),
      withLatestFrom(
        this.store.select(selectStoreForm),
        this.store.select(selectStoreId)
      ),
      filter(([_, storeForm, storeId]) => !!storeForm && !!storeId),
      map(([_, storeForm, storeId]) => ({ storeForm: storeForm as LocalForm, storeId })),
      mergeMap(({ storeForm, storeId }) => {
        this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
        return this.storeService.update(storeId!, storeForm).pipe(
          mergeMap(({data}) => {
            
            const [local] = data;
            
            return [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Store updated successfully',
              },
            }),
            STORE_CREATE_DUPLICATE({ targetLocal: storeForm.storeForCopy, originLocal: local.idLocal }),
            STORE_UPDATE_COMPLETED(),
            STORE_UPDATE_LOADING({ loading: false }),
            STORE_FIND_ALL({}),
          ]}),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              STORE_FAILURE({ error }),
              STORE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  updateComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(STORE_UPDATE_COMPLETED),
        tap(() => {
          this.storeService.updateCompleted();
        })
      ),
    { dispatch: false }
  );

  activate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_ACTIVATE),
      withLatestFrom(this.store.select(selectBusinessSetting)),
      filter(([{ store }, businessSetting]) => !!store && !!businessSetting),
      map(([ { id, store }, businessSetting]) => ({ id, store: store as Local, businessSetting: businessSetting as ConfiguracionGeneral })),
      mergeMap(({ id, store, businessSetting }) => {
        this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
        return this.storeService.activate({ id, storeForm: transformStoreForm({ ...store, configuracionTienda: businessSetting }) }).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Store activated successfully',
              },
            }),
            STORE_ACTIVATE_COMPLETED(),
            STORE_UPDATE_LOADING({ loading: false }),
            STORE_FIND_ALL({}),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              STORE_FAILURE({ error }),
              STORE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  activateComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(STORE_REMOVE_COMPLETED),
        tap(() => {})
      ),
    { dispatch: false }
  );

  deactivate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_DEACTIVATE),
      mergeMap(({ id }) => {
        this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
        return this.storeService.deactivate(id).pipe(
          mergeMap(() => [
            NOTIFICATION_SHOW({
              notification: {
                type: 'success',
                title: 'Success',
                detail: 'Store deactivated successfully',
              },
            }),
            STORE_DEACTIVATE_COMPLETED(),
            STORE_UPDATE_LOADING({ loading: false }),
            STORE_FIND_ALL({}),
          ]),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              STORE_FAILURE({ error }),
              STORE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );

  deactivateComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(STORE_DEACTIVATE_COMPLETED),
        tap(() => {})
      ),
    { dispatch: false }
  );

  duplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_CREATE_DUPLICATE),
      filter(({ originLocal, targetLocal }) => !!originLocal && !!targetLocal),
      mergeMap(({ originLocal, targetLocal }) => {
        return this.storeService.duplicate(originLocal, targetLocal).pipe(
          mergeMap(() => {
            return []
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              STORE_FAILURE({ error }),
            )
          )
        );
      })
    )
  );

  findAllDropdowns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(STORE_FIND_ALL_DROPDOWNS),
      mergeMap(({ accountId }) => {
        this.store.dispatch(STORE_UPDATE_LOADING({ loading: true }));
        return this.storeService.findAll({ accountId }).pipe(
          mergeMap(({ data }) => {
            const storeListDropdowns = data as Local[];
            
            return [
              STORE_FIND_ALL_DROPDOWNS_SUCCESS({ storeListDropdowns }),
              STORE_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) =>
            of(
              SERVICE_FAILURE({ error }),
              STORE_FAILURE({ error }),
              STORE_UPDATE_LOADING({ loading: false })
            )
          )
        );
      })
    )
  );
}
